/**
 * Global Error Handler
 * Captures window.onerror and unhandled promise rejections.
 * Includes ChunkLoadError detection for stale-cache auto-reload after deploys.
 */

import { ApiRequest, API_AUTH_TYPES, API_ROUTES } from '../api';
import { getStoredHouseholdId } from '../helpers/authStorage';

let errorLoggingApi = null;
let initialized = false;
// Set once this page has asked for a reload. Sibling chunk errors from the same
// stale page keep firing until the browser actually unloads it; they are not
// failures of the reload, so they must not be logged as "still failing".
let chunkReloadPending = false;

const CHUNK_RELOAD_KEY = 'chunk_reload_attempted';
const CHUNK_RELOAD_WINDOW_MS = 30_000; // 30 seconds
const BROWSER_EXTENSION_URL_PATTERN = /\b(?:chrome|moz|safari-web)-extension:\/\//i;
// Browsers report this when a resize callback triggers another layout in the same
// frame. The notifications are delivered next frame, so nothing breaks; it is noise.
const RESIZE_OBSERVER_LOOP_PATTERN = /ResizeObserver loop/i;
// A failure that repeats in a loop (e.g. Safari's "Connection to Indexed Database
// server lost", rejected by a library poll every ~800ms) would otherwise post one
// log per occurrence. Throttle recently retained messages once per window.
const REPEAT_LOG_WINDOW_MS = 5 * 60_000; // 5 minutes
const MAX_REPEAT_LOG_ENTRIES = 100;
const lastLoggedAt = new Map();

const isRepeatOfRecentError = (message) => {
    const now = Date.now();
    // Prune on the next error, including suppressed repeats. The cap also bounds
    // retention during a burst of unique messages and keeps this scan small.
    for (const [key, loggedAt] of lastLoggedAt) {
        if (now - loggedAt >= REPEAT_LOG_WINDOW_MS) lastLoggedAt.delete(key);
    }

    const last = lastLoggedAt.get(message);
    if (last !== undefined && now - last < REPEAT_LOG_WINDOW_MS) return true;
    if (lastLoggedAt.size >= MAX_REPEAT_LOG_ENTRIES) {
        // Make room for the new error so its first occurrence remains visible.
        // An evicted message may be logged again within the five-minute window.
        lastLoggedAt.delete(lastLoggedAt.keys().next().value);
    }
    lastLoggedAt.set(message, now);
    return false;
};

export const isBrowserExtensionError = (error, message, source) => {
    const details = [
        message,
        source,
        error?.message,
        error?.stack,
        error?.fileName,
        error?.filename
    ]
        .filter(Boolean)
        .join('\n');

    return BROWSER_EXTENSION_URL_PATTERN.test(details);
};

/**
 * Get browser information
 */
const getBrowserInfo = () => ({
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    vendor: navigator.vendor,
    viewport: {
        width: window.innerWidth,
        height: window.innerHeight
    },
    screen: {
        width: window.screen.width,
        height: window.screen.height
    }
});

// Error telemetry is an authenticated endpoint; without a token it can only 403.
const hasAccessToken = () => Boolean(localStorage.getItem('access_token'));

/**
 * Log error to server
 */
const logErrorToServer = async (errorData) => {
    try {
        if (!hasAccessToken()) {
            console.error('[GLOBAL-ERROR-HANDLER] No access token; skipping server log:', errorData);
            return;
        }

        if (!errorLoggingApi) {
            errorLoggingApi = new ApiRequest(
                API_ROUTES.ERROR_LOGGING,
                API_AUTH_TYPES.USER
            );
        }

        const household_id = getStoredHouseholdId();

        await errorLoggingApi.post({
            endpoint: 'log_error',
            body: {
                household_id,
                browser_info: getBrowserInfo(),
                ...errorData
            }
        });
    } catch (loggingError) {
        console.error('[GLOBAL-ERROR-HANDLER] Failed to log error:', loggingError);
    }
};

/**
 * Detect whether an error is a chunk-load / stale-cache error.
 *
 * Common symptoms after a deploy when the browser still holds old HTML
 * referencing chunk hashes that no longer exist on the server:
 *   1. error.name === 'ChunkLoadError' (webpack)
 *   2. "Loading chunk … failed" message
 *   3. "Unexpected token '<'"  – the server returned an HTML 404 page
 *      instead of the expected JS chunk
 *   4. SyntaxError originating from a chunk file under /static/js/
 */
const isChunkLoadError = (error, message) => {
    const msg = (message || '').toLowerCase();
    const errorName = error?.name || '';

    // 1. Webpack ChunkLoadError
    if (errorName === 'ChunkLoadError') return true;

    // 2. "Loading chunk X failed"
    if (msg.includes('loading chunk') && msg.includes('failed')) return true;

    // 3. Stale chunk returns HTML → "Unexpected token '<'"
    if (msg.includes("unexpected token '<'")) return true;

    // 4. SyntaxError from a chunk file (e.g. /static/js/…)
    if (
        errorName.includes('SyntaxError') &&
        error?.stack &&
        /\/static\/js\//.test(error.stack)
    ) {
        return true;
    }

    return false;
};

/**
 * Handle a detected chunk-load error.
 *
 * Strategy: reload the page exactly once so the browser fetches fresh HTML
 * (and therefore fresh chunk URLs). A sessionStorage timestamp prevents an
 * infinite reload loop if the new chunks also fail.
 *
 * First-time chunk errors are NOT logged to the server — the reload below
 * recovers the user, so logging them just floods error reporting with
 * self-healing events. We only log when a reload has already been attempted
 * recently and the chunk is still failing (the actionable case).
 */
export const handleChunkLoadError = (errorMessage, errorStack) => {
    if (chunkReloadPending) return;

    const now = Date.now();
    const lastAttempt = parseInt(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0', 10);
    const recentlyAttempted = now - lastAttempt < CHUNK_RELOAD_WINDOW_MS;

    if (recentlyAttempted) {
        logErrorToServer({
            error_type: 'chunk_load_error',
            error_message: errorMessage,
            error_stack: errorStack || null,
            url: window.location.href,
            user_action: 'Chunk reload already attempted — still failing',
            metadata: {
                chunkError: true,
                reloadAttempted: false,
                previousAttemptTimestamp: lastAttempt || null
            }
        });
        console.warn(
            '[GLOBAL-ERROR-HANDLER] Chunk load error detected but reload was already attempted recently. Skipping reload to prevent loop.'
        );
        return;
    }

    // First-time chunk error: reload will recover. Skip server logging.
    console.warn('[GLOBAL-ERROR-HANDLER] Chunk load error detected — reloading page for fresh assets.');
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
    chunkReloadPending = true;
    window.location.reload();
};

/**
 * Handle window.onerror
 */
export const handleWindowError = (message, source, lineno, colno, error) => {
    // Check for chunk load errors first
    if (isChunkLoadError(error, message)) {
        handleChunkLoadError(message, error?.stack || `at ${source}:${lineno}:${colno}`);
        return false;
    }

    if (isBrowserExtensionError(error, message, source)) {
        return false;
    }

    if (RESIZE_OBSERVER_LOOP_PATTERN.test(message || '')) {
        return false;
    }

    // Signed-out occurrences are not sent, so they must not start the repeat window.
    if (hasAccessToken() && isRepeatOfRecentError(message)) {
        return false;
    }

    logErrorToServer({
        error_type: 'global_error',
        error_message: message,
        error_stack: error?.stack || `at ${source}:${lineno}:${colno}`,
        url: window.location.href,
        user_action: 'Page interaction',
        metadata: {
            source,
            lineno,
            colno
        }
    });

    // Return false to allow default error handling
    return false;
};

/**
 * Handle unhandled promise rejections
 */
export const handleUnhandledRejection = (event) => {
    const errorMessage = event.reason?.message || String(event.reason);

    // Check for chunk load errors first (lazy-loaded chunks reject as promises)
    if (isChunkLoadError(event.reason, errorMessage)) {
        handleChunkLoadError(errorMessage, event.reason?.stack || null);
        return;
    }

    if (isBrowserExtensionError(event.reason, errorMessage)) {
        return;
    }

    // Signed-out occurrences are not sent, so they must not start the repeat window.
    if (hasAccessToken() && isRepeatOfRecentError(errorMessage)) {
        return;
    }

    logErrorToServer({
        error_type: 'global_error',
        error_message: errorMessage,
        error_stack: event.reason?.stack || null,
        url: window.location.href,
        user_action: 'Promise rejection',
        metadata: {
            promiseRejection: true,
            reason: String(event.reason)
        }
    });
};

/**
 * Handle errors caught by route-level ErrorBoundary components.
 * Called via the onError prop of react-error-boundary's <ErrorBoundary>.
 * Logs to the server and attempts to capture to Sentry.
 *
 * @param {Error} error - The error that was thrown.
 * @param {{ componentStack?: string }} errorInfo - React error info with component stack.
 */
export const handleBoundaryError = (error, errorInfo) => {
    // React lazy()/Suspense forwards chunk-load failures to the nearest
    // ErrorBoundary as a thrown error, so they reach this handler instead of
    // window.onerror or unhandledrejection. Detect and trigger the same
    // reload-once recovery before falling through to normal logging.
    const errorMessage = error?.message || error?.toString() || '';
    if (isChunkLoadError(error, errorMessage)) {
        handleChunkLoadError(errorMessage, error?.stack || errorInfo?.componentStack || null);
        return;
    }

    logErrorToServer({
        error_type: 'component_error',
        error_message: error?.message || error?.toString() || 'Unknown error',
        error_stack: error?.stack || errorInfo?.componentStack || null,
        url: window.location.href,
        user_action: 'Component rendered',
        metadata: {
            componentStack: errorInfo?.componentStack || null
        }
    });

    try {
        import("@sentry/react")
            .then(Sentry => Sentry.captureException(error))
            .catch(() => {});
    } catch (e) {
        // Sentry may not be loaded — ignore
    }
};

/**
 * Initialize global error handlers
 */
export const initializeGlobalErrorHandler = () => {
    if (initialized) {
        console.warn('[GLOBAL-ERROR-HANDLER] Already initialized');
        return;
    }

    // Set up window.onerror
    window.addEventListener('error', (event) => {
        handleWindowError(
            event.message,
            event.filename,
            event.lineno,
            event.colno,
            event.error
        );
    });

    // Set up unhandled rejection handler
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    initialized = true;
    console.log('[GLOBAL-ERROR-HANDLER] Initialized');
};

/**
 * Cleanup handlers (for hot reload in development)
 */
export const cleanupGlobalErrorHandler = () => {
    window.removeEventListener('error', handleWindowError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    initialized = false;
};

export default {
    initializeGlobalErrorHandler,
    cleanupGlobalErrorHandler,
    handleBoundaryError
};

